import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";

import {
  buildSourceCacheFromToolOutputs,
  type NeoSource,
} from "../../../pages/Neo/citations/neoSources";
import SourceDrawer from "../../../pages/Neo/citations/SourceDrawer";
import { useMedRecVisible } from "../../../pages/Neo/citations/useMedRecVisible";
import { trackNeoEvent } from "../../../pages/Neo/neoAnalytics";
import { useExclusiveDrawer } from "../../drawerCoordinator";
import {
  CopilotCitationContext,
  type CopilotCitationContextValue,
} from "./citationContext";
import type { CopilotToolCall } from "./types";
import styles from "./styles.module.scss";

/** The tools' outputs, keeping the previous array while every output is the
 *  same object. Grouping rebuilds ``message.tools`` on each streamed delta;
 *  without this every delta would rebuild the lookup and re-render every
 *  markdown block in the message. */
const useStableToolOutputs = (
  tools: CopilotToolCall[] | undefined
): unknown[] => {
  const previous = useRef<unknown[]>([]);
  const next = (tools ?? []).map((tool) => tool.output);
  if (
    next.length !== previous.current.length ||
    next.some((output, index) => output !== previous.current[index])
  ) {
    previous.current = next;
  }
  return previous.current;
};

/** Resolves an assistant message's ``[[source:…]]`` markers against the
 *  source candidates in its own tool outputs (via the shared Neo lookup
 *  builder), and owns the drawer a clicked chip opens. */
const MessageCitations = ({
  tools,
  children,
}: {
  tools: CopilotToolCall[] | undefined;
  children: ReactNode;
}) => {
  const medRecVisible = useMedRecVisible();
  const outputs = useStableToolOutputs(tools);
  const sourceCache = useMemo(
    () => buildSourceCacheFromToolOutputs(outputs),
    [outputs]
  );
  const [drawerSources, setDrawerSources] = useState<NeoSource[]>([]);
  const [drawerClaim, setDrawerClaim] = useState("");
  const closeDrawer = useCallback(() => {
    setDrawerSources([]);
    setDrawerClaim("");
  }, []);
  useExclusiveDrawer(drawerSources.length > 0, closeDrawer);

  const onOpen = useCallback(
    (ids: string[], claim?: string) => {
      const list = ids
        .map((id) => sourceCache[id])
        .filter((source): source is NeoSource => !!source);
      if (!list.length) return;
      setDrawerSources(list);
      setDrawerClaim(claim ?? "");
      trackNeoEvent("citation chip opened", {
        source_count: list.length,
        source_types: list.map((source) => source.source_type),
        has_claim: !!claim,
      });
    },
    [sourceCache]
  );
  const value = useMemo<CopilotCitationContextValue>(
    () => ({
      resolve: (id: string) => sourceCache[id],
      medRecVisible,
      onOpen,
    }),
    [sourceCache, medRecVisible, onOpen]
  );

  return (
    <CopilotCitationContext.Provider value={value}>
      {children}
      <SourceDrawer
        sources={drawerSources}
        claim={drawerClaim}
        onClose={closeDrawer}
        rootClassName={styles.sourceDrawerRoot}
      />
    </CopilotCitationContext.Provider>
  );
};

export default MessageCitations;
